"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  applyTheme,
  getSystemTheme,
  persistPreference,
  readStoredPreference,
  resolveTheme,
  SSR_THEME,
  type Theme,
  type ThemePreference,
} from "@/lib/theme";

interface ThemeContextValue {
  /** Resolved appearance used by UI (light or dark). */
  theme: Theme;
  /** Stored user preference including system default. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export type { Theme, ThemePreference };

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>("system");
  const [theme, setThemeState] = useState<Theme>(SSR_THEME);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = readStoredPreference();
    const resolved = resolveTheme(stored);
    // Sync React state with DOM already set by THEME_INIT_SCRIPT on first client paint
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only theme hydration
    setPreferenceState(stored);
    setThemeState(resolved);
    applyTheme(resolved);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || preference !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const onChange = () => {
      const resolved = getSystemTheme();
      setThemeState(resolved);
      applyTheme(resolved);
    };

    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [mounted, preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    persistPreference(next);
    const resolved = resolveTheme(next);
    setThemeState(resolved);
    applyTheme(resolved);
  }, []);

  const toggleTheme = useCallback(() => {
    setPreferenceState((currentPreference) => {
      const currentResolved = resolveTheme(currentPreference);
      const next: ThemePreference =
        currentResolved === "light" ? "dark" : "light";
      persistPreference(next);
      setThemeState(next);
      applyTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      // Use SSR_THEME until mounted so server and client first paint agree (init script handles DOM class).
      theme: mounted ? theme : SSR_THEME,
      preference: mounted ? preference : ("system" as ThemePreference),
      setPreference,
      toggleTheme,
    }),
    [theme, preference, mounted, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
